import type { CanonicalTransaction } from "./types";

const LAST_VISIT_KEY = "trackinsiders.last-visit-filing-date.v1";

export function readLastVisitFilingDate(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(LAST_VISIT_KEY);
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
}

export function writeLastVisitFilingDate(filingDate: string | null | undefined): void {
  if (typeof window === "undefined" || !filingDate) return;
  try {
    window.localStorage.setItem(LAST_VISIT_KEY, filingDate);
  } catch {
    // ignore
  }
}

export function maxFilingDate(rows: readonly CanonicalTransaction[]): string | null {
  let latest: string | null = null;
  for (const row of rows) {
    if (latest === null || row.filingDate > latest) latest = row.filingDate;
  }
  return latest;
}

export function rowsSinceFilingDate(
  rows: readonly CanonicalTransaction[],
  sinceFilingDate: string | null
): CanonicalTransaction[] {
  if (!sinceFilingDate) return [...rows];
  return rows.filter((row) => row.filingDate > sinceFilingDate);
}

export function buildActivityFeedStats(
  rows: readonly CanonicalTransaction[],
  favoriteTickers: readonly string[],
  sinceFilingDate: string | null
) {
  const favorites = new Set(favoriteTickers.map((ticker) => ticker.toUpperCase()));
  const recent = rowsSinceFilingDate(rows, sinceFilingDate);
  const scope = recent.length > 0 ? recent : rows;
  let newHighScoreBuys = 0;
  let newClusters = 0;
  let watchlistHits = 0;
  for (const row of scope) {
    if (row.signal.bucket === "informative_buy" && (row.signal.score ?? 0) >= 75) {
      newHighScoreBuys += 1;
    }
    if (
      row.signal.bucket === "informative_buy" &&
      row.cluster !== null &&
      row.cluster.distinctInsiderCount >= 2
    ) {
      newClusters += 1;
    }
    if (favorites.has(row.ticker.toUpperCase())) {
      watchlistHits += 1;
    }
  }
  const topRows = [...scope]
    .filter((row) => row.signal.bucket === "informative_buy")
    .sort((left, right) => {
      const scoreDelta = (right.signal.score ?? -1) - (left.signal.score ?? -1);
      if (scoreDelta !== 0) return scoreDelta;
      const valueDelta = (right.value ?? -1) - (left.value ?? -1);
      if (valueDelta !== 0) return valueDelta;
      return right.filingDate.localeCompare(left.filingDate);
    })
    .slice(0, 5);

  return {
    latestFilingDate: maxFilingDate(rows),
    newHighScoreBuys,
    newClusters,
    watchlistHits,
    topRows,
    usedFallbackToFullSet: recent.length === 0
  };
}
